import appConfig from "@config/app";
import { GeoFilter, toString } from "hassibot/component/filter/geography";
import {
  AgencyUuid,
  CollaboratorDsitUuid,
  CollaboratorTaskUuid,
  DSITJob,
  EventVocalUuid,
  Filters,
  Mail,
  Pager,
  Sort,
} from "hassibot/services_v2/common/types";
import { filterWriter, forgeFiltersParams } from "hassibot/services_v2/common/writer";
import { DateTime } from "luxon";

import { prependUri, toQueryString } from "../common";

import { CollaboratorTasksFilters } from "./types";

class CollaboratorRoutes {
  collaborators = ({ filters }: { pager?: Pager; filters?: Filters; sort?: Sort }): string => {
    const params = {
      ...(filters ? filterWriter(filters) : {}),
    };
    return `?${toQueryString(params)}`;
  };

  events = (from: DateTime, to: DateTime, geo?: GeoFilter[], owners?: Mail[]): string => {
    return `/events?${toQueryString({
      fromDatetime: from.toISO(),
      toDatetime: to.toISO(),
      ...(geo ? { agencies: toString(geo) } : {}),
      ...(owners && owners.length > 0 ? { owners: owners.join(",") } : {}),
    })}`;
  };

  listVocals = (): string => `/vocal-notes`;

  deleteVocal = (vocalUuid: EventVocalUuid): string => `/vocal-notes/${vocalUuid}`;

  updateVocal = (vocalUuid: EventVocalUuid): string => `/vocal-notes/${vocalUuid}`;

  collaboratorsTasks = ({
    filters,
    pager,
    sort,
  }: {
    filters?: CollaboratorTasksFilters;
    pager?: Pager;
    sort?: Sort;
  }): string => {
    const params = forgeFiltersParams({ filters, pager, sort });
    const queryString = toQueryString(params);
    return queryString ? `/tasks?${queryString}` : `/tasks`;
  };
  collaboratorsTask = (taskUuid: CollaboratorTaskUuid): string => `/tasks/${taskUuid}`;
  collaboratorsAgencies = (
    agencyUuids: AgencyUuid[],
    jobs?: DSITJob[],
    maxPostingRank?: number
  ): string =>
    `/collaborators-agencies?${toQueryString({
      agencyUuids: (agencyUuids || []).join(","),
      ...(jobs && jobs.length > 0 && { jobs: jobs.join(",") }),
      ...(maxPostingRank && { maxPostingRank: maxPostingRank.toString() }),
    })}`;
  contractorCollaboratorsAgencies = (
    postalCode?: string,
    inseeCode?: string,
    dsitJobs?: DSITJob[]
  ): string =>
    `/collaborators-agencies/search-contractors-by-location?${toQueryString({
      ...(postalCode ? { postalCode: postalCode } : {}),
      ...(inseeCode ? { inseeCode: inseeCode } : {}),
      ...(dsitJobs && { dsitJobs: dsitJobs.join(",") }),
    })}`;

  chatSettings = (collaboratorDsitUuid: CollaboratorDsitUuid): string =>
    `/${collaboratorDsitUuid}/chat-settings`;

  collaboratorNotifications = (createdAtAfter: DateTime): string => {
    const params = toQueryString({ createdAtAfter: createdAtAfter.toISO() });
    return `/notifications?${params}`;
  };

  collaboratorNotificationsReadAll = (): string => `/notifications/read-all`;

  collaboratorNotificationRead = (notificationUuid: string): string =>
    `/notifications/${notificationUuid}/read`;

  me = (): string => `/me`;

  collaboratorsLeaves = (
    dsitUuids: CollaboratorDsitUuid[],
    overlapStartsAt?: DateTime,
    overlapEndsAt?: DateTime
  ): string =>
    `/leaves?${toQueryString({
      dsitUuids: dsitUuids.join(","),
      ...(overlapStartsAt && {
        overlapStartsAt: overlapStartsAt.toISO({ includeOffset: false }),
      }),
      ...(overlapEndsAt && { overlapEndsAt: overlapEndsAt.toISO({ includeOffset: false }) }),
    })}`;
}

class CollaboratorAsyncRoutes {
  sendVocal = (): string => `/vocal-notes`;
  calendarEvents = (
    collaboratorDsitUuid: CollaboratorDsitUuid,
    timeMin: DateTime,
    timeMax: DateTime
  ): string =>
    `/${collaboratorDsitUuid}/calendar/events?${toQueryString({
      timeMin: timeMin.toISO(),
      timeMax: timeMax.toISO(),
    })}`;
}

export const routes = prependUri(`${appConfig.API_BASE_URL as string}/2/collaborators`)(
  new CollaboratorRoutes()
);

export const asyncRoutes = prependUri(
  `${appConfig.ASYNC_API_BASE_URL as string}/async/2/collaborators`
)(new CollaboratorAsyncRoutes());
